import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import GlassCard from '../components/GlassCard';
import Button from '../components/Button';
import Badge from '../components/Badge';
import { apiService, FlightSchedule, ScheduleStats, UploadResult } from '../services/api';

const Schedules = () => {
  const [schedules, setSchedules] = useState<FlightSchedule[]>([]);
  const [stats, setStats] = useState<ScheduleStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    departureAirport: '',
    arrivalAirport: '',
    aircraftType: '',
  });
  const [replaceExisting, setReplaceExisting] = useState(false);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getFlightSchedules({
        start_date: filters.startDate || undefined,
        end_date: filters.endDate || undefined,
        departure_airport: filters.departureAirport || undefined,
        arrival_airport: filters.arrivalAirport || undefined,
        aircraft_type: filters.aircraftType || undefined,
        limit: 100,
      });
      setSchedules(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiService.getScheduleStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
    fetchStats();
  }, [fetchSchedules, fetchStats]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setError(null);

    try {
      const result = await apiService.uploadFlightSchedule(file, replaceExisting);
      setUploadResult(result);
      if (result.success) {
        fetchSchedules();
        fetchStats();
      }
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [replaceExisting, fetchSchedules, fetchStats]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      departureAirport: '',
      arrivalAirport: '',
      aircraftType: '',
    });
  };

  const formatTime = (isoString: string) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDate = (isoString: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Flight Schedules</h1>
          <p className="text-slate-400 mt-1">Upload and manage flight schedule data</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <GlassCard className="p-4">
            <div className="text-sm text-slate-400">Total Schedules</div>
            <div className="text-2xl font-bold text-white">{stats.total_schedules}</div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="text-sm text-slate-400">Active Schedules</div>
            <div className="text-2xl font-bold text-green-400">{stats.active_schedules}</div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="text-sm text-slate-400">Unique Routes</div>
            <div className="text-2xl font-bold text-blue-400">{stats.unique_routes}</div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="text-sm text-slate-400">Aircraft Types</div>
            <div className="text-2xl font-bold text-purple-400">{stats.aircraft_types.join(', ') || '-'}</div>
          </GlassCard>
        </div>
      )}

      {/* Upload Section */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Upload Flight Schedule</h2>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-500/10'
              : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
          }`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-3">
            <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {isDragActive ? (
              <p className="text-blue-400">Drop the CSV file here...</p>
            ) : (
              <>
                <p className="text-slate-300">Drag & drop a CSV file here, or click to select</p>
                <p className="text-sm text-slate-500">Supported format: CSV with flight schedule data</p>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
            />
            Replace existing schedules
          </label>
        </div>

        {uploading && (
          <div className="mt-4 flex items-center gap-3 text-blue-400">
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Uploading and processing...
          </div>
        )}

        {uploadResult && (
          <div className={`mt-4 p-4 rounded-lg ${uploadResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              {uploadResult.success ? (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className={uploadResult.success ? 'text-green-400' : 'text-red-400'}>
                {uploadResult.message}
              </span>
            </div>
            <div className="text-sm text-slate-400 space-y-1">
              <p>Total rows: {uploadResult.total_rows}</p>
              <p>Imported: {uploadResult.imported}</p>
              <p>Skipped: {uploadResult.skipped}</p>
              {uploadResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-red-400">Errors:</p>
                  <ul className="list-disc list-inside text-xs text-red-300 max-h-32 overflow-y-auto">
                    {uploadResult.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
            {error}
          </div>
        )}

        {/* Expected Format */}
        <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Expected CSV Format</h3>
          <code className="text-xs text-slate-400 block whitespace-pre-wrap">
ACTIVE,FLIGHT_ID,DEPART_DATE,FLIGHT_NUMBER,STD,FROM,TO,STA,MAX_SEATS,AIRCRAFT_TYPE
true,195361,01/01/2026,2T240,08:00,BDA,EWR,09:30,96,SCHED190
          </code>
        </div>
      </GlassCard>

      {/* Filters */}
      <GlassCard className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">From</label>
            <input
              type="text"
              placeholder="e.g., BDA"
              value={filters.departureAirport}
              onChange={(e) => handleFilterChange('departureAirport', e.target.value.toUpperCase())}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm w-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={4}
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">To</label>
            <input
              type="text"
              placeholder="e.g., EWR"
              value={filters.arrivalAirport}
              onChange={(e) => handleFilterChange('arrivalAirport', e.target.value.toUpperCase())}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm w-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={4}
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Aircraft</label>
            <select
              value={filters.aircraftType}
              onChange={(e) => handleFilterChange('aircraftType', e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value="E175">E175</option>
              <option value="E190">E190</option>
            </select>
          </div>
          <Button variant="secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button onClick={fetchSchedules}>
            Apply Filters
          </Button>
        </div>
      </GlassCard>

      {/* Schedule Table */}
      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Flight</th>
                <th className="px-4 py-3 font-medium">Route</th>
                <th className="px-4 py-3 font-medium">Departure</th>
                <th className="px-4 py-3 font-medium">Arrival</th>
                <th className="px-4 py-3 font-medium">Block Time</th>
                <th className="px-4 py-3 font-medium">Aircraft</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    Loading schedules...
                  </td>
                </tr>
              ) : schedules.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    No flight schedules found. Upload a CSV to get started.
                  </td>
                </tr>
              ) : (
                schedules.map((schedule) => (
                  <tr key={schedule.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 text-white whitespace-nowrap">
                      {formatDate(schedule.operating_date)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-blue-400">{schedule.flight_number}</span>
                    </td>
                    <td className="px-4 py-3 text-white">
                      <span className="font-medium">{schedule.departure_airport}</span>
                      <span className="text-slate-500 mx-2">→</span>
                      <span className="font-medium">{schedule.arrival_airport}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 font-mono">
                      {formatTime(schedule.scheduled_departure)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 font-mono">
                      {formatTime(schedule.scheduled_arrival)}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {Math.floor(schedule.block_time_minutes / 60)}h {schedule.block_time_minutes % 60}m
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={schedule.aircraft_type === 'E190' ? 'info' : 'default'}>
                        {schedule.aircraft_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={schedule.is_active ? 'success' : 'warning'}>
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {schedules.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-700 text-sm text-slate-400">
            Showing {schedules.length} schedules
            {stats && ` of ${stats.total_schedules} total`}
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default Schedules;
